import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useQueryParam, StringParam } from 'use-query-params';

import {
  Box,
  Button,
  Card,
  CardActions,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Typography,
  ButtonGroup,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody
} from '@material-ui/core';
import {
  ViewList as ViewListIcon,
  ViewColumn as ViewColumnIcon
} from '@material-ui/icons';

import SearchBox from '../../common/SearchBox';
import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TrainingCreateDialog from './dialogs/TrainingCreateDialog';
import TrainingTaskStatusLabels from './components/TrainingTaskStatusLabels';
import { useFindTrainings, useFindTrainingVersion } from './Training.hooks';
import { ApiParams } from '../../common/api/api';

enum ViewEnum {
  List = 'list',
  Board = 'board'
}

const StyledBox = styled(Box)`
  .MuiButtonBase-root:not(:last-child) {
    margin-right: 8px;
  }
`;

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 140px;
  width: 100%;
  padding: 16px;
  align-items: center;
  justify-content: center;
  .training-id {
    line-height: unset;
    margin-bottom: 8px;
  }
  .title {
    margin-bottom: 4px;
  }
`;
const StyledStatusContainer = styled.div`
  display: flex;
  margin-top: 5px;
  .status-label {
    margin-right: 8px;
  }
`;

const TrainingAdminListPage: React.FC = () => {
  const history = useHistory();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [view, setView] = useQueryParam('view', StringParam);
  const [order = 'id', setOrder] = useQueryParam('order', StringParam);
  const [direction = 'desc', setDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const [previewTrainingId, setPreviewTrainingId] = useState<
    number | undefined
  >();
  const { trainingList, isLoading, error, get } = useFindTrainings();
  const {
    trainingVersion,
    isLoading: isFindingVersion,
    findTrainingVersion
  } = useFindTrainingVersion(previewTrainingId || 0, 'latest');

  const loadTrainings = useCallback(() => {
    const params: ApiParams = { filter: search, order: String(order || '') };
    if (order && direction) {
      params.order = `${order} ${direction}`;
    }
    get(params);
  }, [get, search, order, direction]);

  useEffect(() => {
    loadTrainings();
  }, [loadTrainings]);

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'asc' ? 'desc' : 'asc');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'desc' ? 'desc' : 'asc';
  };

  useEffect(() => {
    if (typeof previewTrainingId !== 'undefined') {
      findTrainingVersion();
    }
  }, [previewTrainingId, findTrainingVersion]);

  useEffect(() => {
    if (typeof previewTrainingId !== 'undefined' && trainingVersion) {
      history.push(
        `/portal/training/${previewTrainingId}/version/${trainingVersion.id}`
      );
    }
  }, [history, previewTrainingId, trainingVersion]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Training Administration</h2>
        <Box ml={2}>
          <ButtonGroup variant='contained' color='primary'>
            <Button size='small' onClick={() => setView(ViewEnum.Board)}>
              <ViewColumnIcon />
            </Button>
            <Button size='small' onClick={() => setView(ViewEnum.List)}>
              <ViewListIcon />
            </Button>
          </ButtonGroup>
        </Box>
        <SearchBox value={search} onSearch={value => setSearch(value)} />
        <PageTemplateHeaderActions>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => setIsOpenDialog(true)}
          >
            Add Training
          </Button>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateDashboard>
        {view === ViewEnum.List ? (
          //   <TaskBoard
          //   entity={WorkflowEntity.Training}
          //   onSelectTask={task => {
          //     const taskId = task.parent?.id || task.id;
          //     history.push(`/portal/training/task/${taskId}`);
          //   }}
          // />
          <PageTemplateCardOver>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={order === 'id'}
                        direction={getSortDirection('id')}
                        onClick={() => handleSort('id')}
                      >
                        ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={order === 'name'}
                        direction={getSortDirection('name')}
                        onClick={() => handleSort('name')}
                      >
                        Training Name/Title
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={order === 'status'}
                        direction={getSortDirection('status')}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Preview</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trainingList &&
                    trainingList.data.map(training => (
                      <TableRow
                        hover
                        key={training.id}
                        onClick={() =>
                          history.push(`./training/task/${training.task?.id}`)
                        }
                      >
                        <TableCell>TRAINING-{training.id}</TableCell>
                        <TableCell>{training.name}</TableCell>
                        <TableCell>
                          <StyledStatusContainer>
                            <TrainingTaskStatusLabels training={training} />
                          </StyledStatusContainer>
                        </TableCell>
                        <TableCell>
                          {training.version > 1 && (
                            <Button
                              color='primary'
                              variant='outlined'
                              disabled={isFindingVersion}
                              onClick={event => {
                                event.stopPropagation();
                                setPreviewTrainingId(training.id);
                              }}
                            >
                              Preview
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </PageTemplateCardOver>
        ) : (
          <Grid container spacing={3}>
            {isLoading && (
              <Grid item xs={12}>
                <Box
                  height={300}
                  width='100%'
                  display='flex'
                  alignItems='center'
                  justifyContent='center'
                >
                  <CircularProgress />
                </Box>
              </Grid>
            )}
            {trainingList &&
              trainingList.data.map(training => (
                <Grid item xs={12} md={4} key={training.id}>
                  <Card>
                    {isFindingVersion && previewTrainingId === training.id && (
                      <LinearProgress />
                    )}
                    <StyledCardContent>
                      <Typography
                        variant='overline'
                        color='textSecondary'
                        className='training-id'
                      >
                        TRAINING-{training.id}
                      </Typography>
                      <Typography variant='body1' className='title'>
                        {training.name}
                      </Typography>
                      <StyledStatusContainer>
                        <TrainingTaskStatusLabels training={training} />
                      </StyledStatusContainer>
                    </StyledCardContent>
                    <Divider />
                    <CardActions>
                      <StyledBox
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        width='100%'
                        padding='10px'
                      >
                        {training.version > 1 && (
                          <Button
                            color='primary'
                            variant='outlined'
                            disabled={isFindingVersion}
                            onClick={() => setPreviewTrainingId(training.id)}
                          >
                            Preview
                          </Button>
                        )}
                        <Button
                          component={Link}
                          variant='outlined'
                          color='primary'
                          to={`./training/task/${training.task?.id}`}
                        >
                          Edit Training
                        </Button>
                      </StyledBox>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
          </Grid>
        )}
      </PageTemplateDashboard>
      <AppSnackbar severity='error' open={Boolean(error)}>
        There was an error getting data from the server. Please check your
        internect connection.
      </AppSnackbar>
      {isOpenDialog && (
        <TrainingCreateDialog
          open={isOpenDialog}
          onCancel={() => setIsOpenDialog(false)}
          onCreated={() => {
            setIsOpenDialog(false);
            loadTrainings(); // refresh data
          }}
        />
      )}
    </PageTemplate>
  );
};

export default TrainingAdminListPage;
